// ═══════════════════════════════════════════════════════════════
// Currency Utilities
// Shared currency formatting for all theme components
// ═══════════════════════════════════════════════════════════════

// Currency symbol to ISO code mapping (Arabic symbols → ISO 4217)
const CURRENCY_MAP: Record<string, string> = {
    // North Africa
    'د.ج': 'DZD',  // دينار جزائري
    'د.م': 'MAD',  // درهم مغربي
    'د.ت': 'TND',  // دينار تونسي
    'ج.م': 'EGP',  // جنيه مصري
    'د.ل': 'LYD',  // دينار ليبي

    // Gulf Countries
    'ر.س': 'SAR',  // ريال سعودي
    'د.إ': 'AED',  // درهم إماراتي
    'ر.ق': 'QAR',  // ريال قطري
    'د.ك': 'KWD',  // دينار كويتي
    'ر.ع': 'OMR',  // ريال عماني
    'د.ب': 'BHD',  // دينار بحريني
    'ر.ي': 'YER',  // ريال يمني

    // Levant
    'ل.س': 'SYP',  // ليرة سورية
    'ل.ل': 'LBP',  // ليرة لبنانية
    'د.أ': 'JOD',  // دينار أردني


    // Other
    '₺': 'TRY',    // ليرة تركية
    '$': 'USD',    // دولار
    '€': 'EUR',    // يورو
    '£': 'GBP',    // جنيه استرليني
};

/**
 * Format a price with proper currency
 * Handles Arabic currency symbols and invalid ISO codes gracefully
 */
export function formatPrice(price: number, currency: string = 'DZD'): string {
    // Convert local currency symbols to ISO codes
    const isoCurrency = CURRENCY_MAP[currency] || currency;

    // Validate ISO code (must be 3 uppercase letters)
    const validCurrency = /^[A-Z]{3}$/.test(isoCurrency) ? isoCurrency : 'DZD';

    try {
        // Use Arabic locale by default to get localized currency symbols
        // and replace the trailing dot from 'د.ج.' to prevent RTL BiDi visual glitches (.د.ج)
        return new Intl.NumberFormat('ar-DZ', {
            style: 'currency',
            currency: validCurrency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price).replace('د.ج.', 'د.ج');
    } catch {
        // Fallback: just show price with currency symbol
        return `${price.toLocaleString('ar-DZ')} ${currency}`;
    }
}

/**
 * Get the ISO currency code from a symbol or return as-is
 */
export function getCurrencyCode(currency: string): string {
    return CURRENCY_MAP[currency] || currency;
}

/**
 * Format a converted price with ≈ prefix
 * Shows approximate conversion when display currency differs from base
 */
export function formatConvertedPrice(
    price: number,
    baseCurrency: string,
    displayCurrency: string
): string {
    const baseISO = getCurrencyCode(baseCurrency);
    const displayISO = getCurrencyCode(displayCurrency);

    if (baseISO === displayISO) return formatPrice(price, baseCurrency);

    // Dynamic import to avoid circular deps — use inline conversion
    const { convertCurrency } = require('@/themes/core/exchange-rates');
    const converted = convertCurrency(price, baseISO, displayISO);
    return `≈ ${formatPrice(converted, displayCurrency)}`;
}

export default { formatPrice, getCurrencyCode, formatConvertedPrice, CURRENCY_MAP };
